import { useQueries } from "convex/react";
import { makeFunctionReference } from "convex/server";
import { useMemo } from "react";
import { isConvexConfigured } from "@/lib/convex";

export type LifestyleShot = "front" | "detail" | "full";

export interface LifestyleImage {
  designKey: string;
  sku: string;
  color: string;
  url: string;
  width: number;
  height: number;
  shot: LifestyleShot;
  sortOrder: number;
}

const SHOTS = new Set<string>(["front", "detail", "full"]);

const LIFESTYLE_QUERY = makeFunctionReference<"query", Record<string, never>, LifestyleImage[]>("merchLifestyle:listPublic");

const EMPTY: LifestyleImage[] = [];

function isLifestyleImage(value: unknown): value is LifestyleImage {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.designKey === "string" &&
    typeof row.sku === "string" &&
    typeof row.color === "string" &&
    typeof row.url === "string" &&
    /^https:\/\//.test(row.url) &&
    typeof row.width === "number" &&
    typeof row.height === "number" &&
    typeof row.shot === "string" &&
    SHOTS.has(row.shot) &&
    typeof row.sortOrder === "number"
  );
}

export function useLifestyleImages(enabled = true): LifestyleImage[] {
  const active = enabled && isConvexConfigured;
  const queries = useMemo(() => (active ? { lifestyle: { query: LIFESTYLE_QUERY, args: {} } } : {}), [active]);
  const results = useQueries(queries);
  const value: unknown = results.lifestyle;
  return useMemo(() => {
    if (!Array.isArray(value)) return EMPTY;
    const rows = value.filter(isLifestyleImage);
    return rows.length > 0 ? rows : EMPTY;
  }, [value]);
}

export function lifestyleFor(images: LifestyleImage[], designKeys: string[], sku: string, color?: string | null): LifestyleImage[] {
  const keys = new Set(designKeys);
  const matches = images.filter((image) => keys.has(image.designKey) && image.sku === sku);
  if (matches.length === 0) return EMPTY;
  const wanted = color ? matches.filter((image) => image.color === color) : [];
  const pool = wanted.length > 0 ? wanted : matches;
  return [...pool].sort((a, b) => a.sortOrder - b.sortOrder);
}
